import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const FooterScroll = () => {
    const sectionRef = useRef(null);
    const columnsRef = useRef([]);
    const bottomRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Columns staggered animation with slide up effect
            columnsRef.current.forEach((column, index) => {
                gsap.fromTo(
                    column,
                    {
                        opacity: 0,
                        y: 50,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: column,
                            start: 'top 90%',
                            end: 'top 60%',
                            scrub: 1,
                        },
                    }
                );
            });

            // Bottom section fade in
            gsap.fromTo(
                bottomRef.current,
                {
                    opacity: 0,
                    y: 30,
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: bottomRef.current,
                        start: 'top 95%',
                        end: 'top 70%',
                        scrub: 1,
                    },
                }
            );
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <footer
            ref={sectionRef}
            className="bg-black py-16 text-white relative overflow-hidden"
        >
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
                    {/* About */}
                    <div ref={(el) => (columnsRef.current[0] = el)}>
                        <a href="#home" className="flex items-center gap-2 text-2xl font-extrabold text-white mb-6">
                            Swan <span className="text-primary-red">Global</span>
                        </a>
                        <p className="text-gray-400 leading-relaxed">
                            Swan Global is a trusted study abroad consultancy in Hyderabad, helping students achieve their international education goals with expert guidance and high visa success rate.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div ref={(el) => (columnsRef.current[1] = el)}>
                        <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
                        <ul className="space-y-3">
                            {['Home', 'About', 'Services', 'Contact'].map((link) => (
                                <li key={link}>
                                    <a
                                        href={`#${link.toLowerCase()}`}
                                        className="text-gray-400 hover:text-primary-red transition-colors duration-300"
                                    >
                                        {link}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Countries */}
                    <div ref={(el) => (columnsRef.current[2] = el)}>
                        <h4 className="text-lg font-semibold mb-6">Countries</h4>
                        <ul className="space-y-3">
                            {['Australia', 'UK', 'USA', 'Canada', 'New Zealand'].map((country) => (
                                <li key={country}>
                                    <a
                                        href="#destinations"
                                        className="text-gray-400 hover:text-primary-red transition-colors duration-300"
                                    >
                                        {country}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div ref={(el) => (columnsRef.current[3] = el)}>
                        <h4 className="text-lg font-semibold mb-6">Contact Info</h4>
                        <div className="space-y-4">
                            <p className="flex items-center gap-3 text-gray-400">
                                <i className="fas fa-phone text-primary-red"></i>
                                9908260456 / 9000329999
                            </p>
                            <p className="flex items-center gap-3 text-gray-400">
                                <i className="fas fa-map-marker-alt text-primary-red"></i>
                                613, 6th floor, Fairmount Downtown, Kompally, Secunderabad, Hyderabad, Telangana- 500100
                            </p>
                            <p className="flex items-center gap-3 text-gray-400">
                                <i className="fas fa-envelope text-primary-red"></i>
                                info@swanglobaledu.com
                            </p>
                            <p className="flex items-center gap-3 text-gray-400">
                                <i className="fas fa-globe text-primary-red"></i>
                                www.swanglobaledu.com
                            </p>
                        </div>
                        <div className="flex gap-4 mt-6">
                            {[
                                { icon: 'facebook-f', href: 'https://www.facebook.com/SwanAbroad', label: 'Facebook' },
                                { icon: 'instagram', href: 'https://www.instagram.com/swan_abroad_education/', label: 'Instagram' },
                                { icon: 'linkedin-in', href: 'https://www.linkedin.com/', label: 'LinkedIn' },
                                { icon: 'twitter', href: 'https://twitter.com/', label: 'Twitter' },
                            ].map((social) => (
                                <a
                                    key={social.icon}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={social.label}
                                    className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-primary-red transition-all duration-300"
                                >
                                    <i className={`fab fa-${social.icon}`}></i>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                <div ref={bottomRef} className="pt-8 border-t border-white/10 text-center">
                    <p className="text-gray-400">© Swan Global | A Unit of Swan Abroad Education</p>
                    <p className="text-gray-500 text-sm mt-2">Corporate Office: 613, 6th floor, Fairmount Downtown, Kompally, Secunderabad, Hyderabad, Telangana- 500100</p>
                    <p className="text-gray-500 text-sm mt-1">Website: www.swanglobaledu.com | Email: info@swanglobaledu.com</p>
                    <p className="text-gray-500 text-sm mt-1">Ph: 9908260456 / 9000329999</p>
                </div>
            </div>
        </footer>
    );
};

export default FooterScroll;
